/**
 * Currency formatting helpers with a unit-explicit contract.
 *
 * The backend stores money two ways today: `Payment.amount` is integer **cents**,
 * while `Invoice.amount`, `*.unitPrice`, and `ServiceCatalog.basePrice` are **Float dollars**.
 * To avoid the 100x-display bug that comes from an ambiguously-named `formatCurrency`,
 * the unit is encoded in the function name — pick the one that matches your value's unit.
 *
 * Prefer `formatCents` for new code: integer minor units are the target representation
 * for the deferred money-unification work (see Docs/planning/inconsistency-audit.md).
 */

const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/** Format an integer-cents amount as USD. e.g. formatCents(5000) -> "$50.00". */
export const formatCents = (cents: number): string => USD.format((cents ?? 0) / 100);

/** Format a dollars amount as USD. e.g. formatDollars(50) -> "$50.00". */
export const formatDollars = (dollars: number): string => USD.format(dollars ?? 0);

/** Convert a dollars float to integer cents (rounded). */
export const toCents = (dollars: number): number => Math.round((dollars ?? 0) * 100);

/** Convert integer cents to a dollars number. */
export const fromCents = (cents: number): number => (cents ?? 0) / 100;

/**
 * Round dollars to the cent: the web's ONE line-total rounding (INV-112). Every line it builds
 * (the invoice editor, barn operations, the barn visit estimate) stores qty x price through this.
 */
export const roundDollars = (dollars: number): number => Math.round(dollars * 100) / 100;
